(() => {
  const SELECTOR = '[data-afrp-root]';

  const normalize = (value = '') => String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-');

  const cleanEngraving = (value = '', max = 25) => String(value || '')
    .replace(/[^A-Za-zÄÖÜäöüß0-9 .&'’♥-]/g, '')
    .replace(/\s{2,}/g, ' ')
    .slice(0, max);

  const safeText = (value, fallback = '', max = 25) => {
    const cleaned = cleanEngraving(value, max).trim();
    return cleaned || fallback;
  };

  function init(root) {
    if (!root || root.dataset.afrpReady === 'true') return;
    root.dataset.afrpReady = 'true';

    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => Array.from(root.querySelectorAll(selector));
    const readJson = (selector, fallback) => {
      const node = q(selector);
      if (!node) return fallback;
      try { return JSON.parse(node.textContent); } catch (error) { return fallback; }
    };

    const variants = readJson('[data-afrp-ring-variants]', []);
    const necklaceVariants = readJson('[data-afrp-necklace-variants]', []);
    const stoneAssets = readJson('[data-afrp-stone-assets]', { left: {}, right: {} });
    const currency = root.dataset.currency || 'EUR';
    const routesRoot = root.dataset.routesRoot || '/';
    const discountPercent = Number(root.dataset.discountPercent || 20);
    const discountCode = String(root.dataset.discountCode || '').trim();
    const outerAddonId = Number(root.dataset.outerAddonId || 0);
    const innerAddonId = Number(root.dataset.innerAddonId || 0);
    const outerAddonPrice = Number(root.dataset.outerAddonPrice || 500);
    const innerAddonPrice = Number(root.dataset.innerAddonPrice || 500);
    const outerAddonAvailable = root.dataset.outerAddonAvailable === 'true';
    const innerAddonAvailable = root.dataset.innerAddonAvailable === 'true';

    const monthLabels = {
      januar: 'Januar · Granat',
      februar: 'Februar · Amethyst',
      maerz: 'März · Aquamarin',
      april: 'April · Klarer Kristall',
      mai: 'Mai · Smaragd',
      juni: 'Juni · Alexandrit',
      juli: 'Juli · Rubin',
      august: 'August · Peridot',
      september: 'September · Saphir',
      oktober: 'Oktober · Opal',
      november: 'November · Topas',
      dezember: 'Dezember · Zirkon'
    };

    const formatMoney = (cents) => {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2).replace('.', ',')} €`;
      }
    };

    const leftStone = q('[data-afrp-left-stone]');
    const rightStone = q('[data-afrp-right-stone]');
    const leftLayer = q('[data-afrp-left-layer]');
    const rightLayer = q('[data-afrp-right-layer]');
    const outerToggle = q('[data-afrp-outer-toggle]');
    const outerPanel = q('[data-afrp-outer-panel]');
    const leftName = q('[data-afrp-left-name]');
    const rightName = q('[data-afrp-right-name]');
    const innerToggle = q('[data-afrp-inner-toggle]');
    const innerPanel = q('[data-afrp-inner-panel]');
    const innerText = q('[data-afrp-inner-text]');
    const previewLeftName = q('[data-afrp-preview-left-name]');
    const previewRightName = q('[data-afrp-preview-right-name]');
    const previewInnerPath = q('[data-afrp-preview-inner]');
    const innerPreview = q('[data-afrp-inner-preview]');
    const previewMonths = q('[data-afrp-preview-months]');
    const previewNames = q('[data-afrp-preview-names]');
    const chipLeft = q('[data-afrp-chip-left]');
    const chipRight = q('[data-afrp-chip-right]');
    const chipOuter = q('[data-afrp-chip-outer]');
    const chipInner = q('[data-afrp-chip-inner]');
    const ringOptionSelects = qa('[data-afrp-ring-option]');
    const necklaceMonth = q('[data-afrp-necklace-month]');
    const necklaceAvailability = q('[data-afrp-necklace-availability]');
    const addRingButton = q('[data-afrp-add-ring]');
    const addBundleButton = q('[data-afrp-add-bundle]');
    const mobileButton = q('[data-afrp-mobile-add]');
    const message = q('[data-afrp-message]');

    const ringBaseNodes = qa('[data-afrp-ring-base-price]');
    const ringTotalNodes = qa('[data-afrp-ring-total]');
    const outerPriceRows = qa('[data-afrp-outer-row]');
    const innerPriceRows = qa('[data-afrp-inner-row]');
    const bundleRingNodes = qa('[data-afrp-bundle-ring-price]');
    const bundleNecklaceNodes = qa('[data-afrp-bundle-necklace-price]');
    const bundleRegularNodes = qa('[data-afrp-bundle-regular]');
    const bundleDiscountNodes = qa('[data-afrp-bundle-discount]');
    const bundleTotalNodes = qa('[data-afrp-bundle-total]');
    const bundleSavingNodes = qa('[data-afrp-bundle-saving]');

    let ringVariant = null;
    let necklaceVariant = null;
    let submitting = false;

    const showMessage = (text = '') => {
      if (message) message.textContent = text;
    };

    const selectedLabel = (select) => select?.selectedOptions?.[0]?.textContent?.trim() || '';

    const findRingVariant = () => {
      if (!variants.length) return null;
      if (!ringOptionSelects.length) return variants.find((item) => item.available) || null;
      return variants.find((variant) => ringOptionSelects.every((select) => {
        const position = Number(select.dataset.optionPosition || 1) - 1;
        return String(variant.options?.[position] || '') === String(select.value || '');
      })) || null;
    };

    const findNecklaceVariant = (monthKey) => {
      if (!necklaceVariants.length) return null;
      const monthWord = String(monthLabels[monthKey] || '').split('·')[0].trim();
      const aliases = monthKey === 'maerz' ? ['maerz', 'marz'] : [monthKey];
      const needles = [...aliases, normalize(monthWord), normalize(monthLabels[monthKey] || '')].filter(Boolean);
      const exact = necklaceVariants.find((variant) => {
        const haystack = normalize([variant.title, ...(variant.options || [])].join(' '));
        return needles.some((needle) => haystack.includes(needle));
      });
      return exact || null;
    };

    const addonTotal = () => {
      let total = 0;
      if (outerToggle?.checked) total += outerAddonPrice;
      if (innerToggle?.checked) total += innerAddonPrice;
      return total;
    };

    const updatePreview = () => {
      const leftKey = leftStone?.value || 'dezember';
      const rightKey = rightStone?.value || 'juli';
      if (leftLayer && stoneAssets.left?.[leftKey]) leftLayer.src = stoneAssets.left[leftKey];
      if (rightLayer && stoneAssets.right?.[rightKey]) rightLayer.src = stoneAssets.right[rightKey];

      const outerEnabled = Boolean(outerToggle?.checked);
      const innerEnabled = Boolean(innerToggle?.checked);
      const leftValue = safeText(leftName?.value, 'Dein Name', 12);
      const rightValue = safeText(rightName?.value, 'Lieblingsmensch', 12);
      const innerValue = safeText(innerText?.value, '', 25);

      if (outerPanel) outerPanel.hidden = !outerEnabled;
      if (innerPanel) innerPanel.hidden = !innerEnabled;
      if (previewLeftName) { previewLeftName.textContent = outerEnabled ? leftValue : ''; previewLeftName.style.fontSize = `${Math.max(32, 64 - Math.max(0, leftValue.length - 5) * 4)}px`; }
      if (previewRightName) { previewRightName.textContent = outerEnabled ? rightValue : ''; previewRightName.style.fontSize = `${Math.max(32, 64 - Math.max(0, rightValue.length - 5) * 4)}px`; }
      if (previewInnerPath) previewInnerPath.textContent = innerEnabled ? innerValue : '';
      if (innerPreview) innerPreview.hidden = !innerEnabled;
      if (previewMonths) previewMonths.textContent = `${monthLabels[leftKey]} + ${monthLabels[rightKey]}`;
      if (previewNames) previewNames.textContent = outerEnabled ? `${leftValue} & ${rightValue}` : 'Ohne Außengravur';
      if (chipLeft) chipLeft.textContent = `Links: ${monthLabels[leftKey]}`;
      if (chipRight) chipRight.textContent = `Rechts: ${monthLabels[rightKey]}`;
      if (chipOuter) chipOuter.textContent = outerEnabled ? 'Außengravur aktiv' : 'Ohne Außengravur';
      if (chipInner) chipInner.textContent = innerEnabled ? 'Innengravur aktiv' : 'Ohne Innengravur';

      updatePrices();
    };

    const updateNecklace = () => {
      const monthKey = necklaceMonth?.value || 'januar';
      necklaceVariant = findNecklaceVariant(monthKey);
      if (necklaceAvailability) {
        const available = Boolean(necklaceVariant?.available);
        necklaceAvailability.textContent = available
          ? `✓ ${monthLabels[monthKey]} ist verfügbar`
          : `Diese Monatsvariante ist aktuell nicht verfügbar.`;
        necklaceAvailability.style.color = available ? '' : '#9b3434';
      }
      updatePrices();
    };

    const updatePrices = () => {
      ringVariant = findRingVariant();
      const ringBase = Number(ringVariant?.price || 0);
      const necklaceBase = Number(necklaceVariant?.price || 0);
      const extras = addonTotal();
      const ringTotal = ringBase + extras;
      const eligibleSecondPrice = ringBase && necklaceBase ? Math.min(ringBase, necklaceBase) : 0;
      const discount = Math.round(eligibleSecondPrice * discountPercent / 100);
      const bundleRegular = ringBase + necklaceBase + extras;
      const bundleTotal = bundleRegular - discount;

      ringBaseNodes.forEach((node) => { node.textContent = ringVariant ? formatMoney(ringBase) : '–'; });
      ringTotalNodes.forEach((node) => { node.textContent = ringVariant ? formatMoney(ringTotal) : '–'; });
      outerPriceRows.forEach((node) => { node.hidden = !outerToggle?.checked; });
      innerPriceRows.forEach((node) => { node.hidden = !innerToggle?.checked; });
      bundleRingNodes.forEach((node) => { node.textContent = ringVariant ? formatMoney(ringBase) : '–'; });
      bundleNecklaceNodes.forEach((node) => { node.textContent = necklaceVariant ? formatMoney(necklaceBase) : '–'; });
      bundleRegularNodes.forEach((node) => { node.textContent = ringVariant && necklaceVariant ? formatMoney(bundleRegular) : '–'; });
      bundleDiscountNodes.forEach((node) => { node.textContent = ringVariant && necklaceVariant ? `− ${formatMoney(discount)}` : '–'; });
      bundleTotalNodes.forEach((node) => { node.textContent = ringVariant && necklaceVariant ? formatMoney(bundleTotal) : '–'; });
      bundleSavingNodes.forEach((node) => { node.textContent = ringVariant && necklaceVariant ? `Du sparst ${formatMoney(discount)} auf den günstigeren Schmuckartikel.` : ''; });

      const outerReady = !outerToggle?.checked || (outerAddonId > 0 && outerAddonAvailable);
      const innerReady = !innerToggle?.checked || (innerAddonId > 0 && innerAddonAvailable);
      const ringReady = Boolean(ringVariant?.available && outerReady && innerReady);
      if (addRingButton) addRingButton.disabled = submitting || !ringReady;
      if (mobileButton) mobileButton.disabled = submitting || !ringReady;
      if (addBundleButton) addBundleButton.disabled = submitting || !ringReady || !necklaceVariant?.available;
    };

    const validate = (bundle = false) => {
      if (!ringVariant?.available) return 'Die gewählte Ringausführung ist derzeit nicht verfügbar.';
      if (outerToggle?.checked) {
        if (!safeText(leftName?.value, '', 12) || !safeText(rightName?.value, '', 12)) return 'Bitte trage beide Namen für die Außengravur ein.';
        if (!outerAddonId) return 'Das 5-€-Produkt für die Außengravur ist noch nicht mit dem Theme verbunden.';
        if (!outerAddonAvailable) return 'Die Außengravur ist derzeit nicht verfügbar.';
      }
      if (innerToggle?.checked) {
        if (!safeText(innerText?.value, '', 25)) return 'Bitte trage den Text für die Innengravur ein.';
        if (!innerAddonId) return 'Das 5-€-Produkt für die Innengravur ist noch nicht mit dem Theme verbunden.';
        if (!innerAddonAvailable) return 'Die Innengravur ist derzeit nicht verfügbar.';
      }
      if (bundle && !necklaceVariant?.available) return 'Bitte wähle einen verfügbaren Geburtsmonat für die Kette.';
      return '';
    };

    const ringProperties = (configId) => ({
      'Linker Geburtsstein': selectedLabel(leftStone),
      'Rechter Geburtsstein': selectedLabel(rightStone),
      'Außengravur': outerToggle?.checked ? 'Ja' : 'Nein',
      'Name links': outerToggle?.checked ? safeText(leftName?.value, '', 12) : '',
      'Name rechts': outerToggle?.checked ? safeText(rightName?.value, '', 12) : '',
      'Innengravur': innerToggle?.checked ? safeText(innerText?.value, '', 25) : 'Nein',
      '_Minaluneva Gruppe': configId,
      '_Minaluneva Rolle': 'ring',
      '_Außengravur Variant-ID': outerToggle?.checked ? String(outerAddonId) : '',
      '_Innengravur Variant-ID': innerToggle?.checked ? String(innerAddonId) : ''
    });

    const buildRingItems = () => {
      const configId = `ML-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const items = [{ id: Number(ringVariant.id), quantity: 1, properties: ringProperties(configId) }];
      if (outerToggle?.checked && outerAddonId) {
        items.push({
          id: outerAddonId,
          quantity: 1,
          properties: {
            'Name links': safeText(leftName?.value, '', 12),
            'Name rechts': safeText(rightName?.value, '', 12),
            '_Minaluneva Gruppe': configId,
            '_Minaluneva Rolle': 'aussengravur'
          }
        });
      }
      if (innerToggle?.checked && innerAddonId) {
        items.push({
          id: innerAddonId,
          quantity: 1,
          properties: {
            'Text Innengravur': safeText(innerText?.value, '', 25),
            '_Minaluneva Gruppe': configId,
            '_Minaluneva Rolle': 'innengravur'
          }
        });
      }
      return items;
    };

    const addItems = async (items, applyDiscount = false) => {
      const endpoint = `${routesRoot.replace(/\/$/, '')}/cart/add.js`;
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Die Auswahl konnte nicht hinzugefügt werden.');
      const cartPath = `${routesRoot.replace(/\/$/, '')}/cart`;
      window.location.href = cartPath;
    };

    const submitRing = async () => {
      const error = validate(false);
      if (error) { showMessage(error); return; }
      submitting = true; showMessage(''); updatePrices();
      try { await addItems(buildRingItems(), false); }
      catch (err) { submitting = false; showMessage(err.message || 'Bitte versuche es erneut.'); updatePrices(); }
    };

    const submitBundle = async () => {
      const error = validate(true);
      if (error) { showMessage(error); return; }
      submitting = true; showMessage(''); updatePrices();
      const items = buildRingItems();
      items.push({
        id: Number(necklaceVariant.id),
        quantity: 1,
        properties: {
          'Geburtsmonat': selectedLabel(necklaceMonth),
          'Aktion': `${discountPercent} % auf den günstigeren Schmuckartikel`,
          '_Minaluneva Set': 'Ring + Kette'
        }
      });
      try { await addItems(items, true); }
      catch (err) { submitting = false; showMessage(err.message || 'Bitte versuche es erneut.'); updatePrices(); }
    };

    [leftStone, rightStone, outerToggle, innerToggle].forEach((element) => element?.addEventListener('change', updatePreview));
    [leftName, rightName, innerText].forEach((element) => element?.addEventListener('input', () => { const max = Number(element.maxLength || 25); const cleaned = cleanEngraving(element.value, max); if (element.value !== cleaned) element.value = cleaned; updatePreview(); }));
    ringOptionSelects.forEach((select) => select.addEventListener('change', updatePrices));
    necklaceMonth?.addEventListener('change', updateNecklace);
    addRingButton?.addEventListener('click', submitRing);
    mobileButton?.addEventListener('click', submitRing);
    addBundleButton?.addEventListener('click', submitBundle);

    ringVariant = findRingVariant();
    updateNecklace();
    updatePreview();
  }

  const initAll = (scope = document) => scope.querySelectorAll(SELECTOR).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
